import { lazy, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/utils/api';
import { PageFrame } from '@/components/executive/ExecAtoms';
import { DealerAssignments } from './DealerAssignments';
import { useProfileResource } from '../dashboard/roles/useProfileResource';
import { DeskState } from '../dashboard/roles/ProfileDeskParts';
const DealerManagementPage = lazy(() => import('./DealerManagementPage').then(module => ({ default: module.DealerManagementPage })));
const dealer = z.object({ id: z.string(), name: z.string(), city: z.string().nullable(), phone: z.string().nullable(), managerUserId: z.string().nullable(), managerName: z.string().nullable() });
const parseList = (body: unknown) => z.object({ dealers: z.array(dealer), total: z.number() }).parse(body);
const parseDealer = (body: unknown) => z.object({ dealer }).parse(body).dealer;
const parseManagers = (body: unknown) => z.object({ data: z.array(z.object({ id: z.string(), name: z.string() })) }).parse(body).data;

/** Keep the existing dealer route focused on P03's primary-owner authority. */
export function TriageDealerRoute() {
  const user = useAuthStore(s => s.user);
  return user?.role?.name === 'lead_triage_coordinator'
    ? <TriageDealers key={`${user.tenantId}:${user.id}`} /> : <DealerManagementPage />;
}
function TriageDealers() {
  const location = useLocation();
  const [search, setSearch] = useState('');
  // SUN-48: the list reloads when the person pauses typing, not on every key.
  const [query, setQuery] = useState('');
  useEffect(() => { const t = setTimeout(() => setQuery(search), 350); return () => clearTimeout(t); }, [search]);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string | null>(location.state?.dealerId ?? null);
  const resource = useProfileResource(`/dealers?limit=50&offset=${page * 50}&q=${encodeURIComponent(query.trim())}`, parseList);
  return <PageFrame title="Dealers" sub="All dealers and their primary owners" actions={<button onClick={resource.reload}>Refresh</button>}>
    <input aria-label="Search dealers" placeholder="Search dealers" value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} className="mb-4 rounded border p-2" />
    <DeskState resource={resource} />
    {resource.data && <>
      <p className="mb-3">{resource.data.total} dealers</p>
      <div className="overflow-x-auto rounded border bg-white"><table className="w-full text-left text-sm"><thead><tr>
        {['Dealer', 'City', 'Phone', 'Owner', 'Action'].map(label => <th key={label} className="p-3">{label}</th>)}
      </tr></thead><tbody>{resource.data.dealers.map(row => <tr key={row.id} className="border-t">
        <td className="p-3">{row.name}</td><td className="p-3">{row.city || 'Not recorded'}</td><td className="p-3">{row.phone || 'Not recorded'}</td>
        <td className="p-3">{row.managerName || 'No owner'}</td><td className="p-3"><button onClick={() => setSelected(row.id)}>Assign owner</button></td>
      </tr>)}</tbody></table>{!resource.data.dealers.length && <p className="p-4">No matching dealers.</p>}</div>
      <div className="my-4 flex gap-4"><button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button><span>Page {page + 1}</span><button disabled={(page + 1) * 50 >= resource.data.total} onClick={() => setPage(p => p + 1)}>Next</button></div>
    </>}
    {selected && <TriageOwnerAssignment key={selected} id={selected} onSaved={resource.reload} onClose={() => setSelected(null)} />}
  </PageFrame>;
}
const NO_OWNER = '__none__';
function TriageOwnerAssignment({ id, onSaved, onClose }: { id: string; onSaved: () => void; onClose: () => void }) {
  const detail = useProfileResource(`/dealers/${id}`, parseDealer);
  const managers = useProfileResource('/dealers/assignable-managers', parseManagers);
  const [owner, setOwner] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setBusy(true); setError(null);
    try { await api.patch(`/dealers/${id}`, { managerUserId: owner === NO_OWNER ? null : owner }); detail.reload(); onSaved(); }
    catch (e: any) { setError(e.response?.data?.error || 'Could not assign the owner.'); }
    finally { setBusy(false); }
  }
  return <section aria-label="Assign dealer owner" className="rounded border bg-white p-4">
    <h3 className="text-lg font-semibold">Assign owner</h3><DeskState resource={detail} /><DeskState resource={managers} />
    {detail.data && <><p>{detail.data.name} · Current owner: {detail.data.managerName || 'No owner'}</p>
      <p className="my-2 text-sm text-gray-500">Dealer identity is read-only.</p>
      <DealerAssignments dealerId={id} canEdit={false} />
    </>}
    {detail.data && managers.data && <div className="my-3 flex gap-3">
      <select aria-label="New owner" value={owner} onChange={event => setOwner(event.target.value)} className="rounded border p-2">
        <option value="">Choose an owner</option><option value={NO_OWNER}>No owner (company-owned)</option>{managers.data.map(manager => <option key={manager.id} value={manager.id}>{manager.name}</option>)}
      </select><button disabled={!owner || busy} onClick={save}>Save owner</button>
    </div>}
    {error && <p role="alert">{error}</p>}<button onClick={onClose}>Close</button>
  </section>;
}
